import React, { ReactElement } from "react";
import { Field, Colors } from "@lib/shared/types";
import { DomainForm } from "src/api/domainForm";
import { useForm } from "../hooks/useForm";
import { Classes, FormProps } from "@components/shared/form";
import { DomainInfo } from "src/api/domainInfo";
import Loader from "@components/shared/loader";
import ArrowRightIcon from "@components/svg/arrowRightIcon";
import WarningAlertIcon from "@components/svg/warningAlertIcon";
import { FormField } from "./FormField";
import { FieldWithValue as InvalidFieldWithValue } from "../hooks/useInvalidFields";
import styles from "@components/shared/form/styles.module.scss";

type UseFormReturn = ReturnType<typeof useForm>;

interface FormErrorsStepProps {
    formErrors: UseFormReturn["formErrors"];
    formErrorsResolved: UseFormReturn["formErrorsResolved"];
    goToThankYouPage: boolean | undefined;
    otp: UseFormReturn["otp"];
    domain: DomainInfo;
    domainNameTranslation: string;
    invalidFields: InvalidFieldWithValue[];
    classes?: Classes;
    isExpandable: (field: Field) => boolean;
    getExpandableFields: (field: Field) => Field[];
    stepStatus: UseFormReturn["stepStatus"];
    isSecondServiceForm: boolean | undefined;
    formProps: FormProps;
    handleFieldBlur: UseFormReturn["handleFieldBlur"];
    handleFieldChange: UseFormReturn["handleFieldChange"];
    fieldsData: UseFormReturn["fieldsData"];
    finalForm: DomainForm;
    searchableMultiSelect: boolean | undefined;
    singleQuestion: boolean | undefined;
    colors: Colors;
    isLastStep: boolean;
    operation: UseFormReturn["operation"];
    isLoading: boolean;
    formSubmitted: boolean;
    currentStep: number;
}

export default function FormErrorsStep({
    formErrors,
    formErrorsResolved,
    goToThankYouPage,
    otp,
    domain,
    domainNameTranslation,
    invalidFields,
    classes,
    isExpandable,
    getExpandableFields,
    stepStatus,
    isSecondServiceForm,
    formProps,
    handleFieldBlur,
    handleFieldChange,
    fieldsData,
    finalForm,
    searchableMultiSelect,
    singleQuestion,
    colors,
    isLastStep,
    operation,
    isLoading,
    formSubmitted,
    currentStep,
}: FormErrorsStepProps): ReactElement {
    return (
        <div className={styles["step"]}>
            <div>
                {/* Start all steps errors */}

                <div className={styles["extra-step-inner"]}>
                    {(formErrors ||
                        (formErrorsResolved &&
                            goToThankYouPage &&
                            !otp?.isRequired)) && (
                        <>
                            <div
                                className={`${styles["warningMessage"]} rounded-sm`}
                                id="all-errors"
                            >
                                <h1
                                    className={`text-base sm:text-lg flex items-start sm:items-center justify-start sm:justify-center`}
                                >
                                    <span className="px-1 pt-1 sm:pt-0">
                                        <WarningAlertIcon />
                                    </span>{" "}
                                    <span>
                                        {domain?.translations?.[
                                            `${domainNameTranslation}_SubmissionHasFewErrors`
                                        ] ?? "Your Submission Has a Few Errors"}
                                    </span>
                                </h1>
                                <p
                                    className={`${styles["description"]}  text-xs sm:text-base `}
                                >
                                    {domain?.translations?.[
                                        `${domainNameTranslation}_PleaseCorrectTheFollowingFields`
                                    ] ??
                                        `You are
                                                                    almost done!
                                                                    Please
                                                                    correct the
                                                                    following
                                                                    fields so we
                                                                    can process
                                                                    your
                                                                    request:`}
                                </p>
                            </div>
                            <div
                                className={`${styles["fields"]} ${
                                    classes?.fieldsWrapperClassName ?? ""
                                }`}
                            >
                                {invalidFields.map((field) => (
                                    <FormField
                                        key={field.codeName}
                                        stepStatus={stepStatus}
                                        field={field as Field}
                                        fields={
                                            isExpandable(field as Field)
                                                ? getExpandableFields(
                                                      field as Field,
                                                  )
                                                : []
                                        }
                                        asTitle={false}
                                        isSecondServiceForm={
                                            isSecondServiceForm ?? false
                                        }
                                        {...formProps}
                                        handleFieldBlur={handleFieldBlur}
                                        handleFieldChange={handleFieldChange}
                                        fieldsData={fieldsData}
                                        form={finalForm}
                                        searchableMultiSelect={
                                            searchableMultiSelect
                                        }
                                        singleQuestion={singleQuestion}
                                    />
                                ))}
                                {formErrorsResolved &&
                                    goToThankYouPage &&
                                    formErrors === 0 && (
                                        <div className="text-center">
                                            <div
                                                className={styles["line-1"]}
                                            ></div>
                                            <div
                                                className={styles["line-2"]}
                                            ></div>
                                            <div
                                                className={styles["line-2"]}
                                            ></div>
                                            <div
                                                className={styles["line-3"]}
                                            ></div>
                                        </div>
                                    )}
                            </div>

                            <button
                                style={{
                                    backgroundColor: colors.primaryColor,
                                }}
                                className={`${styles["submit-button"]} ${
                                    classes?.formButtonClassName ?? ""
                                } ${
                                    isLastStep
                                        ? classes?.formSubmitButtonClassName ??
                                          ""
                                        : ""
                                }`}
                                type="submit"
                            >
                                {operation?.loadingBuyers ||
                                isLoading ||
                                (isLastStep && formSubmitted) ? (
                                    <Loader size={30} />
                                ) : (
                                    <>
                                        <span>
                                            {domain?.translations?.[
                                                `${domainNameTranslation}_SubmitButtonText`
                                            ] ?? "Submit"}
                                        </span>
                                        {currentStep === 0 && (
                                            <span
                                                className={`${styles["arrow"]}`}
                                            >
                                                <ArrowRightIcon />
                                            </span>
                                        )}
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>

                {/* End all steps errors */}
            </div>
        </div>
    );
}
